/**
 * Pages/Panel/ReportesNomina/ReporteXmlEmitidos.js
 *
 * Visor de los CFDI de nómina emitidos de las nóminas cuyo periodo toca el
 * rango de fechas (sin importar la fecha de timbrado ni la de pago).
 * Genera UN archivo con las dos hojas del formato que arma hoy el cliente a
 * mano:
 *
 *   1. "VISOR XML"  — un renglón por CFDI timbrado, con el UUID, el estatus
 *      (Timbrado / Cancelado), el periodo, los datos del receptor y el neto.
 *   2. "CATALOGO"   — el padrón completo de empleados en el layout de 74
 *      columnas del software externo (ver layoutCatalogoExterno.js).
 *
 * A diferencia de la pantalla de Recibos, aquí SÍ aparecen los CFDI cancelados:
 * la columna "Estatus" del formato sólo tiene sentido si están los dos.
 */

import { message, PageHeader, Select, Space, Spin, Tabs } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Form,
  FormItem,
  RangePickerItem,
  SelectItem,
  SwitchItem,
  TableItem,
  ButtonItem,
  ErrorItem,
  dineroDisabledMask,
} from "../../../Components/Items";
import SelectDepartamentosItem from "../../../Components/SelectDepartamentosItem";
import SelectEmpleadosItem from "../../../Components/SelectEmpleadosItem";
import { FileExcelOutlined } from "@ant-design/icons";
import * as XLSXStyle from "xlsx-js-style";
import moment from "moment";
import "moment/locale/es";
import { COLUMNAS_CATALOGO, HEADERS_CATALOGO, ANCHOS_CATALOGO } from "./layoutCatalogoExterno";
import {
  COLUMNAS_VISOR,
  HEADERS_VISOR,
  ANCHOS_VISOR,
  INDICE_TOTAL,
  INDICE_NETO,
} from "./layoutVisorXml";
import {
  headerStyle,
  textoStyle,
  centroStyle,
  numeroStyle,
  monedaStyle,
  totalTextoStyle,
  totalMonedaStyle,
  celda,
  celdaNumero,
  celdaMoneda,
  filaDe,
} from "./estilosExcelNomina";

moment.locale("es");

const { Option } = Select;
const { TabPane } = Tabs;

const OPCIONES_ESTATUS = [
  { value: 1, label: "Solo timbrados" },
  { value: 2, label: "Solo cancelados" },
  { value: 3, label: "Todos" },
];

const OPCIONES_ESTADO_EMPLEADO = [
  { value: 3, label: "Todos" },
  { value: 1, label: "Solo activos" },
  { value: 2, label: "Solo bajas" },
];

class ReporteXmlEmitidos extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    consultado: false,
    empresa: null,
    cfdis: [],
    catalogo: [],
    objetoRegistrosPatronales: null,
  };

  atras() {
    this.props.history.goBack();
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  componentDidMount() {
    axios
      .post(
        "registrosPatronales/selectRegistrosPatronalesItem",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            objetoRegistrosPatronales: res.data.registrosPatronales.map((value) => (
              <Option value={value._id} key={value._id}>
                {value.registroPatronalIMSS}
              </Option>
            )),
          });
        }
      })
      .catch(() => {});
  }

  consultarReporte(values) {
    this.setState({ loading: true });
    const [fechaInicio, fechaFin] = values.periodo || [];
    axios
      .post(
        "reportesNomina/reporteXmlEmitidos",
        {
          empresaId: this.props.empresaId,
          // El rango va contra el periodo de la prenómina (fecha-día), no contra
          // la fecha de timbrado: se manda el día tal cual.
          fechaInicio: fechaInicio ? fechaInicio.format("YYYY-MM-DD") : null,
          fechaFin: fechaFin ? fechaFin.format("YYYY-MM-DD") : null,
          registrosPatronales: values.registrosPatronales || [],
          departamentos: values.departamentos || [],
          empleados: values.empleados || [],
          estatus: values.estatus ?? 3,
          incluirCatalogo: values.incluirCatalogo !== false,
          estadoEmpleados: values.estadoEmpleados ?? 3,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            loading: false,
            consultado: true,
            empresa: res.data.empresa,
            cfdis: res.data.cfdis || [],
            catalogo: res.data.catalogo || [],
          });
          if ((res.data.cfdis || []).length === 0) {
            message.info("No hay CFDI de nóminas con periodo en el rango de fechas");
          }
        } else if (res.data.success === 1) {
          // Tope de filas: el backend dice cómo acotar la consulta.
          message.warning(res.data.message, 8);
          this.setState({ loading: false });
        } else {
          message.error("Ha sucedido un error y no se cargo bien el reporte");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Ha sucedido un error y no se cargo bien el reporte");
        this.setState({ loading: false });
      });
  }

  // ── Hoja 1: visor de XML ───────────────────────────────────────────────────
  hojaVisor() {
    const { cfdis } = this.state;
    const columnas = HEADERS_VISOR.length;
    const filas = [HEADERS_VISOR.map((h) => celda(h, headerStyle))];

    cfdis.forEach((cfdi) => {
      filas.push(
        COLUMNAS_VISOR.map((columna) => {
          const valor = cfdi[columna.clave];
          if (columna.tipo === "moneda") return celdaMoneda(valor, monedaStyle);
          if (columna.tipo === "numero") return celdaNumero(valor, centroStyle);
          if (columna.tipo === "centro") return celda(valor, centroStyle);
          return celda(valor, textoStyle);
        })
      );
    });

    const totales = cfdis.reduce(
      (acumulado, c) => ({
        total: acumulado.total + (c.total || 0),
        neto: acumulado.neto + (c.neto || 0),
      }),
      { total: 0, neto: 0 }
    );
    const filaTotales = filaDe(columnas, totalTextoStyle);
    filaTotales[0] = celda(`TOTALES (${cfdis.length})`, totalTextoStyle);
    filaTotales[INDICE_TOTAL] = celdaMoneda(totales.total, totalMonedaStyle);
    filaTotales[INDICE_NETO] = celdaMoneda(totales.neto, totalMonedaStyle);
    filas.push(filaTotales);

    const hoja = XLSXStyle.utils.aoa_to_sheet(filas);
    hoja["!cols"] = ANCHOS_VISOR.map((wch) => ({ wch }));
    hoja["!rows"] = [{ hpt: 30 }];
    return hoja;
  }

  // ── Hoja 2: catálogo de empleados en el layout externo ─────────────────────
  hojaCatalogo() {
    const { catalogo } = this.state;
    const filas = [HEADERS_CATALOGO.map((h) => celda(h, headerStyle))];

    catalogo.forEach((empleado) => {
      filas.push(
        COLUMNAS_CATALOGO.map((columna) => {
          const valor = columna.clave !== undefined
            ? empleado[columna.clave]
            : columna.constante;
          if (columna.tipo === "moneda") return celdaMoneda(valor, monedaStyle);
          if (columna.tipo === "numero") return celdaNumero(valor, numeroStyle);
          return celda(valor, textoStyle);
        })
      );
    });

    const hoja = XLSXStyle.utils.aoa_to_sheet(filas);
    hoja["!cols"] = ANCHOS_CATALOGO.map((wch) => ({ wch }));
    hoja["!rows"] = [{ hpt: 30 }];
    return hoja;
  }

  descargarExcel() {
    const libro = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(libro, this.hojaVisor(), "VISOR XML");
    if (this.state.catalogo.length > 0) {
      XLSXStyle.utils.book_append_sheet(libro, this.hojaCatalogo(), "CATALOGO");
    }
    XLSXStyle.writeFile(
      libro,
      `XML_Emitidos_Nominas_${moment().format("MMM_YYYY").toUpperCase()}.xlsx`
    );
  }

  render() {
    const { loading, consultado, cfdis, catalogo } = this.state;

    // TableItem fuerza tableLayout fixed e ignora `scroll`, asi que en pantalla
    // van solo las columnas indispensables; las 27 completas van al Excel.
    const columns = [
      { title: "Fecha Comprobante", dataIndex: "fechaComprobante", key: "fechaComprobante", align: "center" },
      { title: "Período", dataIndex: "periodo", key: "periodo", align: "center" },
      { title: "Código", dataIndex: "codigoEmpleado", key: "codigoEmpleado", align: "center" },
      { title: "Nombre Receptor", dataIndex: "nombreReceptor", key: "nombreReceptor" },
      { title: "Departamento", dataIndex: "departamento", key: "departamento" },
      { title: "Fecha Pago", dataIndex: "fechaPago", key: "fechaPago", align: "center" },
      {
        title: "Neto",
        dataIndex: "neto",
        key: "neto",
        align: "right",
        render: (v) => dineroDisabledMask(v || 0),
      },
      {
        title: "Estatus",
        dataIndex: "estatus",
        key: "estatus",
        align: "center",
        render: (v) =>
          v === "Cancelado" ? (
            <span style={{ color: "#cf1322", fontWeight: "bold" }}>{v}</span>
          ) : (
            v
          ),
      },
      { title: "UUID", dataIndex: "uuid", key: "uuid" },
    ];

    return (
      <>
        <Form
          ref={this.formRef}
          name="reporteXmlEmitidos"
          onFinish={this.consultarReporte.bind(this)}
          onFinishFailed={this.onFinishFailed}
          initialValues={{
            periodo: [moment().startOf("month"), moment().endOf("month")],
            estatus: 3,
            estadoEmpleados: 3,
            incluirCatalogo: true,
          }}
        >
          <PageHeader
            onBack={this.atras.bind(this)}
            title="Reporte de XML Emitidos de Nómina"
            style={{
              border: "1px solid rgb(235, 237, 240)",
              backgroundColor: "white",
              marginBottom: 10,
            }}
            extra={
              <div style={{ display: "flex", justifyContent: "right" }}>
                <Space>
                  {cfdis.length > 0 && (
                    <ButtonItem
                      blanco
                      onClick={() => this.descargarExcel()}
                      icon={<FileExcelOutlined />}
                      style={{ display: "block", margin: "0 0 0 auto" }}
                    >
                      Descargar Excel
                    </ButtonItem>
                  )}
                  <ButtonItem
                    type="primary"
                    htmlType="submit"
                    style={{ display: "block", margin: "0 0 0 auto" }}
                  >
                    Consultar
                  </ButtonItem>
                </Space>
              </div>
            }
          />
          <Spin spinning={loading}>
            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingTop: 5,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                marginBottom: 10,
              }}
            >
              {/* forceRender en todas: sin él se pierden los campos de las
                  pestañas que el usuario no llegó a abrir. */}
              <Tabs
                style={{ margin: 0 }}
                tabBarExtraContent={{
                  left: <h1 style={{ padding: 0, margin: "0 10px 0 0" }}>Filtros:</h1>,
                }}
              >
                <TabPane tabKey="1" key="1" forceRender tab="Periodo de la nómina">
                  <FormItem name="periodo" required noStyle>
                    <RangePickerItem />
                  </FormItem>
                  <p style={{ fontSize: 12, color: "#888", margin: "8px 0 0" }}>
                    Entran las nóminas cuyo periodo toca el rango, sin importar la fecha de
                    timbrado ni la de pago.
                  </p>
                </TabPane>
                <TabPane tabKey="2" key="2" forceRender tab="Registro Patronal">
                  <FormItem name="registrosPatronales" noStyle margin>
                    <SelectItem
                      width="90%"
                      mode="multiple"
                      placeholder="Todos los registros patronales"
                    >
                      {this.state.objetoRegistrosPatronales}
                    </SelectItem>
                  </FormItem>
                </TabPane>
                <TabPane tabKey="3" key="3" forceRender tab="Departamentos">
                  <FormItem name="departamentos" noStyle margin>
                    <SelectDepartamentosItem
                      mode="multiple"
                      empresaId={this.props.empresaId}
                      headersToken={this.props.headersToken}
                      placeholder="Todos los departamentos"
                      style={{ width: "90%" }}
                    />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="4" key="4" forceRender tab="Empleados">
                  <FormItem name="empleados" noStyle margin>
                    <SelectEmpleadosItem
                      mode="multiple"
                      incluirBajas
                      empresaId={this.props.empresaId}
                      headersToken={this.props.headersToken}
                      placeholder="Todos los empleados"
                      style={{ width: "90%" }}
                    />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="5" key="5" forceRender tab="Estatus">
                  <FormItem name="estatus" noStyle margin>
                    <SelectItem width={200} placeholder="Estatus del CFDI">
                      {OPCIONES_ESTATUS.map((opcion) => (
                        <Option value={opcion.value} key={opcion.value}>
                          {opcion.label}
                        </Option>
                      ))}
                    </SelectItem>
                  </FormItem>
                </TabPane>
                <TabPane tabKey="6" key="6" forceRender tab="Catálogo de empleados">
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }}>
                    <FormItem
                      name="incluirCatalogo"
                      label="Incluir hoja CATALOGO"
                      valuePropName="checked"
                      margin
                    >
                      <SwitchItem />
                    </FormItem>
                    <FormItem name="estadoEmpleados" label="Empleados del padrón" margin>
                      <SelectItem width={180} placeholder="Todos">
                        {OPCIONES_ESTADO_EMPLEADO.map((opcion) => (
                          <Option value={opcion.value} key={opcion.value}>
                            {opcion.label}
                          </Option>
                        ))}
                      </SelectItem>
                    </FormItem>
                  </div>
                  <p style={{ fontSize: 12, color: "#888", margin: "8px 0 0" }}>
                    El catálogo no depende del rango de fechas, pero sí respeta los filtros de
                    registro patronal, departamentos y empleados.
                  </p>
                </TabPane>
              </Tabs>
            </div>

            {consultado && (
              <div
                style={{
                  backgroundColor: "white",
                  padding: 20,
                  paddingTop: 10,
                  marginBottom: 15,
                  borderRadius: 5,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                }}
              >
                <p style={{ marginBottom: 10, color: "#666" }}>
                  {cfdis.length} CFDI en el rango
                  {catalogo.length > 0 && ` · ${catalogo.length} empleado(s) en el catálogo`}.
                </p>
                <TableItem
                  rowKey={(record) => record.uuid || `${record.codigoEmpleado}-${record.periodo}`}
                  columns={columns}
                  dataSource={cfdis}
                  size="small"
                />
              </div>
            )}
          </Spin>
        </Form>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(ReporteXmlEmitidos);
